import React from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSearchParams } from 'react-router-dom';
import OrdersTable from '../orders/OrdersTable';
import OrderDetails from '../orders/OrderDetails';
import { getOrderStatus, getEquipmentList, getTotalAmount } from '../../utils/orderUtils';
import { useCustomer } from '../../contexts/CustomerContext';

// Shopify order utility functions (from kronk Orders.jsx)
const getShopifyOrderStatus = (order) => {
  if (order.cancelled_at) return 'Cancelled';
  if (order.closed_at) return 'Closed';
  if (order.fulfillment_status === 'fulfilled') return 'Fulfilled';
  if (order.fulfillment_status === 'partial') return 'Partially Fulfilled';
  if (order.financial_status === 'paid') return 'Paid';
  if (order.financial_status === 'pending') return 'Pending Payment';
  if (order.status === 'open') return 'Open';
  return order.status || 'Unknown';
};

const getShopifyOrderStatusStyles = (status) => {
  const statusLower = status?.toLowerCase();
  
  if (statusLower === 'fulfilled' || statusLower === 'paid') {
    return { 
      textColor: 'text-green-500', 
      icon: 'material-symbols:check-circle-outline',
      className: 'text-green-500 tag-green'
    };
  }
  
  if (statusLower === 'pending' || statusLower === 'open' || statusLower === 'partially fulfilled') {
    return { 
      textColor: 'text-yellow-500', 
      icon: 'carbon:incomplete-error',
      className: 'text-yellow-600 tag-orange'
    };
  }
  
  if (statusLower === 'cancelled' || statusLower === 'closed') {
    return { 
      textColor: 'text-error', 
      icon: 'material-symbols:cancel-outline',
      className: 'text-error tag-red'
    };
  }
  
  return { 
    textColor: 'text-font-secondary', 
    icon: 'ph:question',
    className: 'text-font-secondary tag-secondary'
  };
};

const getShopifyLineItems = (order) => {
  if (order.line_items && Array.isArray(order.line_items) && order.line_items.length > 0) {
    return order.line_items
      .map(item => `${item.title}${item.quantity > 1 ? ` (x${item.quantity})` : ''}`)
      .join(', ');
  }
  return 'No items';
};

const getShopifyTotalAmount = (order) => {
  if (order.total_price) {
    return `$${parseFloat(order.total_price).toFixed(2)}`;
  }
  return 'N/A';
};

const formatShopifyDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateString;
  }
};

const OrdersTab = ({ 
  orders, 
  searchQuery, 
  selectedCall, 
  setSelectedCall 
}) => {
  const [searchParams] = useSearchParams();
  const { getEffectiveCustomerId } = useCustomer();
  const customerId = getEffectiveCustomerId();
  const isKronk = customerId === 'kronk';
  const isWards = customerId === 'wards';


  if (!orders || orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon icon="material-symbols:search-off" className="text-6xl text-bgAccent mb-4" />
          <p className="text-font-secondary text-lg">No orders found for "{searchQuery}"</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 gap-2 w-full min-h-0 overflow-hidden flex-col">
      {/* Breadcrumbs - shown when order details are open (only for Wards) */}
      {!isKronk && selectedCall && (
        <div className="flex items-center gap-1 text-font-primary mb-2 flex-shrink-0">
          <button
            onClick={() => setSelectedCall(null)}
            className="flex items-center justify-center rounded-lg hover:bg-bgAccent transition-colors cursor-pointer"
            title="Go back to orders"
          >
            <Icon icon="mdi:chevron-left" className="text-3xl text-accent hover:text-primary transition-colors" />
          </button>
          <span 
            className="flex items-center justify-center gap-1 group transition-colors cursor-pointer"
            onClick={() => setSelectedCall(null)}
          >
            <Icon icon="material-symbols:order-approve-outline" className="text-xl text-font-secondary group-hover:text-primary transition-colors" />
            <span className="text-font-secondary group-hover:text-font-primary transition-colors">Orders</span>
          </span>
          <Icon icon="mdi:chevron-right" className="text-lg text-font-secondary" />
          <span className="flex items-center justify-center gap-1">
            <Icon icon="material-symbols:info-outline" className="text-2xl text-primary" />
            <span className="text-font-primary text-base">
              Order Details
            </span>
          </span>
        </div>
      )}

      <div className="flex flex-1 gap-2 h-full min-h-0">
        {/* Orders List */}
        {isKronk ? (
          // Kronk: Shopify orders table structure
          <div className="flex flex-col w-full transition-all duration-300 ease-out overflow-hidden">
            <div className="w-full h-full flex flex-col overflow-hidden">
              {/* Desktop Table - Custom for Shopify */}
              <div className="flex-shrink-0 hidden md:block">
                <table className="w-full table-auto text-left border-collapse text-sm">
                  <thead>
                    <tr className="text-font-primary h-[48px] rounded-md card-container font-semibold">
                      <th className="w-[200px] max-w-[200px] truncate pl-4 pr-2">Items</th>
                      <th className="w-[120px] max-w-[120px] truncate pr-2">Order #</th>
                      <th className="w-[120px] max-w-[120px] truncate pr-2">Date</th>
                      <th className="w-[100px] max-w-[100px] truncate pr-2">Status</th>
                      <th className="w-[100px] max-w-[100px] truncate pr-2">Total</th>
                      <th className="w-[100px] max-w-[100px] truncate pr-4">Chatbot</th>
                    </tr>
                  </thead>
                </table>
              </div>
              
              <div className="flex-1 overflow-y-auto scrollable hidden md:block">
                <table className="w-full table-auto text-left border-collapse text-sm">
                  <tbody>
                    {orders.map((order) => {
                      return (
                        <tr
                          key={order.id}
                          className="h-[56px] border-b border-border group text-font-secondary transition-all ease-out-in duration-300"
                        >
                          <td className="w-[200px] max-w-[200px] truncate font-semibold pl-4 pr-2 text-font-primary">
                            {getShopifyLineItems(order)}
                          </td>
                          <td className="w-[120px] max-w-[120px] truncate pr-2 text-font-primary">
                            #{order.order_number || order.name}
                          </td>
                          <td className="w-[120px] max-w-[120px] truncate pr-2 group-hover:text-primary">
                            {formatShopifyDate(order.created_at)}
                          </td>
                          <td className="w-[100px] max-w-[100px] truncate pr-2">
                            {(() => {
                              const status = getShopifyOrderStatus(order);
                              const { icon, className } = getShopifyOrderStatusStyles(status);
                              return (
                                <div className={`w-fit font-semibold p-1.5 rounded text-xs flex items-center gap-0.5 text-font-primary justify-center ${className}`}>
                                  <Icon icon={icon} className="text-sm sm:text-base" />
                                  <span className="text-font-primary">{status}</span>
                                </div>
                              );
                            })()}
                          </td>
                          <td className={`w-[100px] max-w-[100px] truncate pr-2 font-semibold text-primary`}>
                            {getShopifyTotalAmount(order)}
                          </td>
                          <td className="w-[100px] max-w-[100px] truncate pr-4">
                            {(() => {
                              const isChatbot = order.utm_tracking?.utm_source === 'chatbot';
                              const icon = isChatbot ? 'material-symbols:check' : 'material-symbols:close';
                              const colorClass = isChatbot ? 'text-primary' : 'text-error';
                              return (
                                <Icon icon={icon} className={`text-sm sm:text-xl ${colorClass}`} />
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="flex-1 overflow-y-auto md:hidden">
                <div className="xl:hidden space-y-2 p-2">
                  {orders.map((order) => {
                    return (
                      <div
                        key={order.id}
                        className="p-3 flex flex-col gap-3 rounded-md transition-all duration-300 ease-out-in bg-background"
                      >
                        <div className="text-base font-semibold flex items-center justify-between">
                          <span className="text-font-primary">
                            {getShopifyLineItems(order)}
                          </span>
                          <span className="text-sm text-font-secondary">
                            #{order.order_number || order.name}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            {(() => {
                              const status = getShopifyOrderStatus(order);
                              const { icon, className } = getShopifyOrderStatusStyles(status);
                              return (
                                <div className={`p-1.5 rounded text-xs flex items-center gap-0.5 text-font-primary justify-center ${className}`}>
                                  <Icon icon={icon} className="text-lg" />
                                  <span className="text-font-primary">{status}</span>
                                </div>
                              );
                            })()}
                            {(() => {
                              const isChatbot = order.utm_tracking?.utm_source === 'chatbot';
                              const icon = isChatbot ? 'material-symbols:check' : 'material-symbols:close';
                              const colorClass = isChatbot ? 'text-primary' : 'text-error';
                              return (
                                <Icon icon={icon} className={`text-lg ${colorClass}`} />
                              );
                            })()}
                          </div>
                          <span className="text-sm font-semibold text-primary">
                            {getShopifyTotalAmount(order)}
                          </span>
                        </div>

                        <div className="flex items-start justify-between">
                          <span className="text-sm text-font-secondary">
                            {formatShopifyDate(order.created_at)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Wards: Use existing OrdersTable component
          <div className={`flex flex-col w-full transition-all duration-300 ease-out overflow-hidden
            ${selectedCall ? 'hidden 2xl:flex 2xl:w-1/2' : 'w-full'}
          `}>
            <div className="w-full h-full flex flex-col">
              <OrdersTable
                paginatedOrders={orders}
                selectedOrder={selectedCall}
                searchParams={searchParams}
                setSelectedOrder={setSelectedCall}
                rowRef={null}
                getEquipmentList={getEquipmentList}
                getOrderStatus={getOrderStatus}
                getTotalAmount={getTotalAmount}
              />
            </div>
          </div>
        )}

        {/* Order Details Panel - Only for Wards */}
        {!isKronk && selectedCall && (
          <OrderDetails 
            selectedOrder={selectedCall}
            searchParams={searchParams}
            setSelectedOrder={setSelectedCall}
            getOrderStatus={getOrderStatus}
            getOrderStatusColor={() => {}} // This will be handled by OrderDetails internally
            handleMarkCompleted={() => {}} // This will be handled by OrderDetails internally
          />
        )}
      </div>
    </div>
  );
};

export default OrdersTab;
